import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { subDays } from 'date-fns';
import { useAuth } from '@/lib/auth';
import { DateRangeSelector } from '@/components/reports/spend/DateRangeSelector';
import { SpendByCategoryChart } from '@/components/reports/spend/SpendByCategoryChart';
import { SpendByVendorChart } from '@/components/reports/spend/SpendByVendorChart';
import { SpendTrendChart } from '@/components/reports/spend/SpendTrendChart';
import { SpendByRequesterTable } from '@/components/reports/spend/SpendByRequesterTable';
import { ExportButton } from '@/components/reports/spend/ExportButton';
import {
  useSpendByCategory,
  useSpendByVendor,
  useSpendTrend,
  useSpendByRequester,
  useSpendExportData,
  useSpendKPIs,
} from '@/hooks/useSpendAnalytics';
import { SpendKPICards } from '@/components/reports/spend/SpendKPICards';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export default function SpendReportPage() {
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const isAdmin = user?.orgRole === 'owner' || user?.orgRole === 'super_user';

  const [startDate, setStartDate] = useState(() => subDays(new Date(), 90));
  const [endDate, setEndDate] = useState(() => new Date());

  const handleDateChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Track report viewed
  useEffect(() => {
    analytics.track({
      name: ANALYTICS_EVENTS.REPORT_VIEWED,
      category: 'engagement',
      properties: {
        report_type: 'spend',
      },
    });
  }, []);

  const { data: categoryData, isLoading: categoryLoading } = useSpendByCategory(
    startDate,
    endDate,
    organizationId
  );

  const { data: vendorData, isLoading: vendorLoading } = useSpendByVendor(
    startDate,
    endDate,
    organizationId
  );

  const { data: trendData, isLoading: trendLoading } = useSpendTrend(
    startDate,
    endDate,
    organizationId
  );

  const { data: requesterData, isLoading: requesterLoading } = useSpendByRequester(
    startDate,
    endDate,
    organizationId,
    user?.id,
    isAdmin
  );

  const { data: exportJobsData, isLoading: exportLoading } = useSpendExportData(
    startDate,
    endDate,
    organizationId
  );

  const { data: kpiData, isLoading: kpiLoading } = useSpendKPIs(
    startDate,
    endDate,
    organizationId
  );

  const isAnyLoading = categoryLoading || vendorLoading || trendLoading || requesterLoading || exportLoading;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/reports">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Spend Report</h1>
            <p className="text-muted-foreground mt-1">
              Analyze procurement spending patterns and budget utilization.
            </p>
          </div>
        </div>
        <ExportButton
          startDate={startDate}
          endDate={endDate}
          categories={categoryData}
          vendors={vendorData}
          requesters={requesterData}
          jobsData={exportJobsData}
          isLoading={isAnyLoading}
        />
      </div>

      {/* Date Range Selector */}
      <DateRangeSelector
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
      />

      {/* KPI Cards */}
      <SpendKPICards data={kpiData} isLoading={kpiLoading} />

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <SpendByCategoryChart data={categoryData} isLoading={categoryLoading} />
        <SpendByVendorChart data={vendorData} isLoading={vendorLoading} />
      </div>

      {/* Trend Chart */}
      <SpendTrendChart data={trendData} isLoading={trendLoading} />

      {/* Requester Table */}
      <SpendByRequesterTable data={requesterData} isLoading={requesterLoading} />
    </div>
  );
}
